import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import usibrasImage from '../assets/images/sistema1-hero.jpg';
import jetwashImage from '../assets/images/Sytem3.1.jpg';
import panamaExteriorImage from '../assets/images/paraguai-industria-textil-exterior.jpeg';
import panamaInteriorImage from '../assets/images/paraguai-industria-textil-interior.jpeg';
import agricultureImage from '../assets/images/sistema_agricola.jpeg';

const projects = [
  {
    id: 'usibras-fortaleza',
    translationKey: 'projects.items.usibras',
    images: [
      { src: usibrasImage, width: 1800, height: 1350, altKey: 'imageAlt' },
    ],
  },
  {
    id: 'jetwash-regua',
    translationKey: 'projects.items.jetwashRegua',
    images: [
      { src: jetwashImage, width: 960, height: 1280, altKey: 'imageAlt' },
    ],
    portrait: true,
  },
  {
    id: 'lubricant-refining-panama',
    translationKey: 'projects.items.lubricantPanama',
    images: [
      {
        src: panamaExteriorImage,
        width: 960,
        height: 1280,
        altKey: 'exteriorImageAlt',
        labelKey: 'exteriorLabel',
      },
      {
        src: panamaInteriorImage,
        width: 1200,
        height: 1600,
        altKey: 'interiorImageAlt',
        labelKey: 'interiorLabel',
      },
    ],
  },
  {
    id: 'swine-industry-agriculture',
    translationKey: 'projects.items.swineAgriculture',
    images: [
      { src: agricultureImage, width: 900, height: 1200, altKey: 'imageAlt' },
    ],
    portrait: true,
  },
];

function Projects() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const activeProject = projects[activeIndex];
  const projectKey = activeProject.translationKey;
  const highlights = t(`${projectKey}.highlights`, { returnObjects: true });

  const showProject = (index) => {
    setActiveIndex((index + projects.length) % projects.length);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showProject(activeIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showProject(activeIndex + 1);
    }
  };

  return (
    <section className="projects-section" id="Projects" aria-labelledby="projects-title">
      <div className="projects-container">
        <header className="projects-heading">
          <p className="projects-eyebrow">{t('projects.eyebrow')}</p>
          <h2 id="projects-title">{t('projects.title')}</h2>
          <p>{t('projects.introduction')}</p>
        </header>

        <div
          className="projects-slider"
          role="region"
          aria-roledescription="carousel"
          aria-label={t('projects.carouselLabel')}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <div className="projects-slider__toolbar">
            <p className="projects-slider__counter" aria-live="polite">
              <strong>{String(activeIndex + 1).padStart(2, '0')}</strong>
              <span aria-hidden="true"> / </span>
              <span>{String(projects.length).padStart(2, '0')}</span>
            </p>

            <div className="projects-slider__arrows">
              <button
                type="button"
                aria-label={t('projects.previous')}
                aria-controls="active-project-slide"
                onClick={() => showProject(activeIndex - 1)}
              >
                <span aria-hidden="true">←</span>
              </button>
              <button
                type="button"
                aria-label={t('projects.next')}
                aria-controls="active-project-slide"
                onClick={() => showProject(activeIndex + 1)}
              >
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>

          <article
            className="project-card"
            id="active-project-slide"
            key={activeProject.id}
            role="group"
            aria-roledescription="slide"
            aria-label={t('projects.slideLabel', {
              current: activeIndex + 1,
              total: projects.length,
            })}
          >
            <figure className={`project-media${activeProject.portrait ? ' project-media--portrait' : ''}${activeProject.images.length > 1 ? ' project-media--gallery' : ''}`}>
              <div className="project-media__images">
                {activeProject.images.map((projectImage) => (
                  <div className="project-media__view" key={projectImage.altKey}>
                    <img
                      src={projectImage.src}
                      width={projectImage.width}
                      height={projectImage.height}
                      alt={t(`${projectKey}.${projectImage.altKey}`)}
                    />
                    {projectImage.labelKey && (
                      <span>{t(`${projectKey}.${projectImage.labelKey}`)}</span>
                    )}
                  </div>
                ))}
              </div>
              <figcaption>{t(`${projectKey}.caption`)}</figcaption>
            </figure>

            <div className="project-content">
              <p className="project-category">{t(`${projectKey}.category`)}</p>
              <h3>{t(`${projectKey}.name`)}</h3>
              {t(`${projectKey}.location`, { defaultValue: '' }) && (
                <p className="project-location">{t(`${projectKey}.location`)}</p>
              )}
              <p className="project-description">{t(`${projectKey}.description`)}</p>

              <ul className="project-highlights">
                {highlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </ul>
            </div>
          </article>

          <div
            className="projects-slider__dots"
            role="group"
            aria-label={t('projects.slideNavigation')}
          >
            {projects.map((project, index) => (
              <button
                type="button"
                key={project.id}
                className={index === activeIndex ? 'is-active' : ''}
                aria-label={t('projects.goToProject', {
                  project: t(`${project.translationKey}.name`),
                })}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => showProject(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Projects;
